import { useSelector } from "react-redux";
import CustomerReviewAction from "@common/redux/actions/CustomerReviewAction";
import ConfirmModalAction from "@common/redux/actions/ConfirmModalAction";
import AlertModalAction from "@common/redux/actions/AlertModalAction";
import ReviewClient from "@common/api/ReviewClient";
import { handleError } from "@common/components/helpers/error";
import CustomerReviewModalAction from "@common/redux/actions/CustomerReviewModalAction";
import { transformReviewQuestions } from "@common/components/helpers/transformReviewQuestions";

function useCustomerReview() {
  const data = useSelector((state) => CustomerReviewAction.getStatePath(state));
  const { selectedRecord, selectedReview, form, questions, originalQuestions, reviewTypeId, currentReviewId, questionSetId, reviewRowVersion } = data;

  const modal = useSelector((state) =>
    CustomerReviewModalAction.getStatePath(state),
  );
  const { selectedCustomer } = modal;

  const setReviewModalState = CustomerReviewAction.dispatch;

  const onChange = (_, { name, value }) =>
    setReviewModalState({ form: { ...form, [name]: value } });

  const onOpenClick = () => {
    onRecordChange(selectedRecord);
    CustomerReviewAction.open();
  };

  const onReviewChange = (_, { value }) =>
    setReviewModalState({ selectedReview: value });

  const onRecordChange = (selectedRecord) => {
    setReviewModalState({
      selectedRecord,
      selectedReview: selectedRecord?.compliance?.reviews?.[0]?.id ?? null,
    });
  };

  const onFormLoad = () => {
    if (!selectedReview) {
      ReviewClient.Create()
        .then((createResponse) => {
          const { record, venueEnterCount, venueEnterTime, ...otherMeta } = createResponse;
          
          const transformedQuestions = transformReviewQuestions(
            record?.questions || [],
            {
              venueEnterCount: venueEnterCount || [],
              venueEnterTime: venueEnterTime || [],
            },
            {},
          );

          const initialForm = {};
          transformedQuestions.forEach((q) => {
            const originalQuestion = record?.questions?.find((oq) => oq.guid === q.guid);
            initialForm[q.name] = originalQuestion?.response !== null && originalQuestion?.response !== undefined 
              ? originalQuestion.response 
              : null;
          });

          setReviewModalState({
            questions: transformedQuestions,
            originalQuestions: record?.questions || [],
            questionSetId: record?.questionSetId,
            reviewTypeId: record?.reviewTypeId ?? 1,
            venueEnterCounts: venueEnterCount || [],
            venueEnterTimes: venueEnterTime || [],
            form: initialForm,
            ...otherMeta,
          });
        })
        .catch((error) => {
          console.error("Failed to load review questions from Review/Create API:", error);
          handleError(error);
        });
      return;
    }

    const reviewId = selectedReview?.record?.id;
    
    Promise.all([
      ReviewClient.Read(reviewId),
      ReviewClient.Create(),
    ])
      .then(([readResponse, createResponse]) => {
        const rawQuestions = readResponse?.questions || [];
        
        const venueEnterCount = createResponse?.venueEnterCount ?? [];
        const venueEnterTime = createResponse?.venueEnterTime ?? [];

        const transformedQuestions = transformReviewQuestions(
          rawQuestions,
          {
            venueEnterCount: Array.isArray(venueEnterCount) ? venueEnterCount : [],
            venueEnterTime: Array.isArray(venueEnterTime) ? venueEnterTime : [],
          },
          readResponse || {},
        );

        const initialForm = {};
        transformedQuestions.forEach((q) => {
          const orig = rawQuestions.find((oq) => oq.guid === q.guid);
          const val = orig?.response;
          initialForm[q.name] = val !== null && val !== undefined ? val : null;
        });

        setReviewModalState({
          questions: transformedQuestions,
          originalQuestions: rawQuestions,
          reviewTypeId: readResponse?.reviewTypeId ?? 1,
          questionSetId: readResponse?.questionSetId ?? null,
          venueEnterCounts: Array.isArray(venueEnterCount) ? venueEnterCount : [],
          venueEnterTimes: Array.isArray(venueEnterTime) ? venueEnterTime : [],
          form: initialForm,
          currentReviewId: readResponse?.id ?? reviewId
        });
      })
      .catch((error) => handleError(error));
  };

  const onConfirmComplete = () => {
    const guidToName = {};
    (questions || []).forEach((q) => {
      if (q.guid) guidToName[q.guid] = q.name;
    });
    const questionsPayload = (originalQuestions || []).map((oq) => ({
      guid: oq.guid,
      ordinal: oq.ordinal,
      type: oq.type,
      question: oq.question,
      parent: oq.parent ?? null,
      condition: oq.condition ?? null,
      response: form[guidToName[oq.guid]] ?? oq.response ?? null,
    }));

    const buildPayload = (reviewId = null, rowVersion = null) => {
      return {
        customerId: selectedCustomer?.id,
        id: reviewId || 0,
        reviewTypeId: reviewTypeId ?? 1,
        questionSetId: questionSetId ?? null,
        questions: questionsPayload,
        rowVersion: rowVersion || null,
      };
    };

    if (!selectedReview?.record?.id) {
      const completePayload = buildPayload();
      ReviewClient.Complete(completePayload)
        .then(() => {
          AlertModalAction.open(
            "Review successfully completed!",
            "Success",
            () => {
              setReviewModalState({ reviewCompleted: true });
              CustomerReviewAction.reset();
            },
          );
        })
        .catch((error) => {
          console.error(error);
          handleError(error);
        });
      return;
    }

    const reviewId = selectedReview?.record?.id;
    const completePayload = buildPayload(reviewId, selectedReview?.record?.rowVersion);
    ReviewClient.Complete(completePayload)
      .then(() => {
        AlertModalAction.open(
          "Review successfully completed!",
          "Success",
          () => {
            setReviewModalState({ reviewCompleted: true });
            CustomerReviewAction.reset();
          },
        );
      })
      .catch((error) => {
        console.error(error);
        handleError(error);
      });
  };

  const onCompleteClick = () => {
    ConfirmModalAction.open(
      "Are you sure you want to complete this review?",
      "Confirm",
      onConfirmComplete,
    );
    setReviewModalState({ inEditMode: false });
  };

  return {
    ...data,
    modal,
    questions,
    onRecordChange,
    onReviewChange,
    onCompleteClick,
    onFormLoad,
    setReviewModalState,
    onOpenClick,
    onChange,
  };
}

export default useCustomerReview;
